import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  contactDetails,
  primaryNavigation,
  studioIdentity,
} from "../../data/site";
import {
  useEscapeKey,
  useFocusTrap,
  useLockBodyScroll,
  useMediaQuery,
  useScrolledPast,
} from "../../hooks";
import "./Header.css";

/** Width at which the desktop navigation replaces the overlay menu. */
const DESKTOP_NAVIGATION_QUERY = "(min-width: 60rem)";

/**
 * Decide whether the header should start in its light (over-image) tone.
 *
 * The header is transparent at the top of the page, so its text colour has to
 * match whatever is behind it. Both the home page and a project detail page
 * open with full-bleed dark media; the project index opens on paper.
 *
 * @param {string} pathname Current location pathname.
 * @returns {"light" | "dark"} "light" means light text over dark media.
 */
function resolveInitialTone(pathname) {
  if (pathname === "/") return "light";
  if (pathname.startsWith("/project/") || pathname.startsWith("/projekat/")) {
    return "light";
  }
  return "dark";
}

/**
 * Site header: wordmark, primary navigation, and the mobile overlay menu.
 *
 * Behaviour worth knowing about:
 *  - It is `position: fixed` and transparent until the page scrolls, at which
 *    point it picks up a paper background and a bottom hairline.
 *  - Over dark media it renders in light text; see `resolveInitialTone`.
 *  - The mobile menu is a full-viewport overlay with a focus trap, Escape
 *    handling and a background scroll lock.
 *
 * @returns {JSX.Element}
 */
function Header() {
  const { pathname } = useLocation();
  const hasScrolled = useScrolledPast(32);
  const isDesktopNavigation = useMediaQuery(DESKTOP_NAVIGATION_QUERY);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  useLockBodyScroll(isMenuOpen);
  useEscapeKey(isMenuOpen, closeMenu);
  const menuContainerRef = useFocusTrap(isMenuOpen);

  // Navigating always dismisses the overlay, including via browser back.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Opening the menu on a phone then rotating or resizing to a desktop width
  // would otherwise leave the scroll lock on behind a hidden overlay.
  useEffect(() => {
    if (isDesktopNavigation) setIsMenuOpen(false);
  }, [isDesktopNavigation]);

  const initialTone = resolveInitialTone(pathname);
  // Once the page is scrolled the header sits on paper, so it is always dark.
  const activeTone = hasScrolled ? "dark" : initialTone;

  const wordmark = (
    <span className="wordmark">
      <span className="wordmark__lead">{studioIdentity.wordmarkLead}</span>
      <span className="wordmark__tail">{studioIdentity.wordmarkTail}</span>
    </span>
  );

  return (
    <>
      <header
        className="site-header"
        data-elevated={hasScrolled ? "true" : "false"}
        data-tone={activeTone}
        data-menu-open={isMenuOpen ? "true" : "false"}
      >
        <div className="site-header__inner">
          <Link
            to="/"
            className="site-header__wordmark"
            aria-label={`${studioIdentity.name} — početna strana`}
            onClick={closeMenu}
          >
            {wordmark}
          </Link>

          <nav className="site-header__nav" aria-label="Glavna navigacija">
            <ul role="list" className="site-header__nav-list">
              {primaryNavigation.map((navigationItem) => (
                <li key={navigationItem.href}>
                  <Link
                    to={navigationItem.href}
                    className="site-header__nav-link"
                  >
                    {navigationItem.label}
                  </Link>
                </li>
              ))}
            </ul>

            <a
              className="site-header__phone"
              href={`tel:${contactDetails.telephoneHref}`}
            >
              {contactDetails.telephoneDisplay}
            </a>
          </nav>

          <button
            type="button"
            className="menu-toggle"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            onClick={() => setIsMenuOpen((wasOpen) => !wasOpen)}
          >
            <span className="menu-toggle__label">
              {isMenuOpen ? "Zatvori" : "Meni"}
            </span>
            <span className="menu-toggle__glyph" aria-hidden="true">
              <span className="menu-toggle__bar" />
              <span className="menu-toggle__bar" />
            </span>
          </button>
        </div>
      </header>

      {/* ---------------------------------------------- mobile overlay menu */}
      <div
        id="mobile-menu"
        className="mobile-menu"
        data-open={isMenuOpen ? "true" : "false"}
        aria-hidden={!isMenuOpen}
        ref={menuContainerRef}
        tabIndex={-1}
      >
        <nav className="mobile-menu__nav" aria-label="Navigacija">
          <ul role="list" className="mobile-menu__list">
            {primaryNavigation.map((navigationItem, itemIndex) => (
              <li
                key={navigationItem.href}
                className="mobile-menu__item"
                style={{ "--item-delay": `${120 + itemIndex * 70}ms` }}
              >
                <Link
                  to={navigationItem.href}
                  className="mobile-menu__link"
                  onClick={closeMenu}
                  tabIndex={isMenuOpen ? 0 : -1}
                >
                  <span className="mobile-menu__index t-numeral">
                    {String(itemIndex + 1).padStart(2, "0")}
                  </span>
                  <span className="mobile-menu__text">{navigationItem.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mobile-menu__footer">
          <p className="t-label mobile-menu__footer-label">Kontakt</p>
          <a
            className="mobile-menu__contact"
            href={`mailto:${contactDetails.emailAddress}`}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            {contactDetails.emailAddress}
          </a>
          <a
            className="mobile-menu__contact"
            href={`tel:${contactDetails.telephoneHref}`}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            {contactDetails.telephoneDisplay}
          </a>
          <p className="mobile-menu__address t-meta">
            {contactDetails.streetAddress}
            <br />
            {contactDetails.postalCode} {contactDetails.city}
          </p>
        </div>
      </div>
    </>
  );
}

export default Header;
